import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { emitApiError } from './apiErrorBus';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Opt a single request out of the global error toast.
     *
     * Use for genuinely optional / fire-and-forget calls whose failure is
     * harmless to the user. Failures are still rejected to the caller.
     */
    suppressErrorToast?: boolean;
  }
}

/** Shown when the server answered with a 5xx and carried no usable message. */
export const SERVER_ERROR_MESSAGE =
  'Une erreur serveur est survenue. Veuillez réessayer.';

/** Shown when the request never got a response (network / CORS / timeout). */
export const NETWORK_ERROR_MESSAGE =
  'Connexion au serveur impossible. Vérifiez votre connexion réseau.';

/**
 * Extracts a user-facing message from an error payload.
 *
 * Prefers the API's own `message` / `error` string; returns `null` when the
 * payload carries nothing usable so the caller can fall back to its own copy.
 * Never returns tokens, credentials or raw URLs — only the API's message text.
 */
function messageFromPayload(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const payload = data as { message?: unknown; error?: unknown };
  const candidate = payload.message ?? payload.error;
  if (typeof candidate === 'string' && candidate.trim() !== '') {
    return candidate;
  }
  return null;
}

/**
 * Creates a configured axios instance with authentication interceptors.
 *
 * The response interceptor also surfaces failures the user would otherwise
 * never see: HTTP 5xx and response-less network errors are emitted on the
 * API error bus (and toasted by `ApiErrorToaster`). 401 redirects as before;
 * 403 / 404 / 422 stay silent — they are handled by the callers and by form
 * validation. Per-request opt-out: `{ suppressErrorToast: true }`.
 *
 * @param tokenKey      - localStorage key for the auth token (e.g. 'admin_auth_token')
 * @param tenantKey     - localStorage key for the tenant ID (e.g. 'admin_active_tenant_id')
 * @returns a fully configured AxiosInstance
 */
export function createApiClient(
  tokenKey: string,
  tenantKey: string,
): AxiosInstance {
  const client = axios.create({
    baseURL: `${import.meta.env.VITE_ORIGAMI_API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.headers['X-Tenant-ID']) {
      const tenantId = localStorage.getItem(tenantKey);
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401) {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(tenantKey);
        globalThis.location.href =
          globalThis.location.pathname +
          globalThis.location.search +
          globalThis.location.hash;
        return Promise.reject(error);
      }

      if (!error.config?.suppressErrorToast) {
        if (status === undefined) {
          // No response at all: network failure, CORS, or timeout.
          emitApiError({ message: NETWORK_ERROR_MESSAGE });
        } else if (status >= 500) {
          emitApiError({
            message:
              messageFromPayload(error.response?.data) ?? SERVER_ERROR_MESSAGE,
            status,
          });
        }
        // 403 / 404 / 422 stay silent by design.
      }

      return Promise.reject(error);
    },
  );

  return client;
}
